import { z } from "zod";
import { Id, IsoDate } from "./common.ts";
import { AiLabels, Channel } from "./domain.ts";
import type { RouteDef } from "./routes.ts";

/**
 * Live dashboards + semantic layer (plan 14 §10).
 *
 * Core idea: a widget stores a *query*, never data. Everything the Copilot renders (chart, stats, table,
 * comments list) carries `source: WidgetQuery`, so pinning a block saves the query and the dashboard
 * re-runs it live. The API compiles a WidgetQuery to parameterized, org-scoped SQL over a whitelisted
 * catalog (DATASETS below); it never accepts raw SQL from the client or an LLM.
 */

// ---------------------------------------------------------------- vocabulary
export const DatasetName = z.enum([
  "comments", "messages", "conversations", "posts", "post_insights", "post_metrics", "account_insights", "automation_runs", "ai_usage", "jobs",
]);
export const MeasureFn = z.enum(["count", "sum", "avg", "min", "max", "p95", "rate", "distinct"]);
/** Time buckets; `hour_of_day` (0–23) and `day_of_week` (0 = Sunday) are cyclical, for heatmaps. */
export const Granularity = z.enum(["hour", "day", "week", "month", "hour_of_day", "day_of_week"]);
export const FilterOp = z.enum(["eq", "neq", "in", "nin", "gt", "gte", "lt", "lte", "contains", "semantic"]);
export const FieldType = z.enum(["id", "string", "enum", "number", "boolean", "time"]);
/**
 * `all` = all time: from the org's earliest data for the query (or the org's creation) to now. It is never rejected for
 * length (no maxRangeDays), takes no comparison, and bucketed time series over it are coarsened (day → week → month) so
 * they stay under QUERY_LIMITS.allTimeBuckets instead of failing.
 */
export const TimePreset = z.enum(["today", "24h", "7d", "30d", "90d", "mtd", "ytd", "all", "custom"]);
export const CompareMode = z.enum(["previous_period", "last_year"]);
export const VizType = z.enum(["kpi", "line", "area", "bar", "stacked_bar", "donut", "heatmap", "table", "funnel", "comments", "posts", "text"]);
export const RefreshMode = z.enum(["live", "30s", "1m", "5m", "manual"]);
export const DashboardScope = z.enum(["org", "account", "post", "campaign"]);
export const DashboardSharing = z.enum(["private", "team"]);
export const TemplateKey = z.enum(["home", "account", "post", "campaign"]);
/** Entity a field's values refer to: the UI shows labels and opens the item on drill-down. */
export const EntityKind = z.enum(["account", "post", "publication", "comment", "conversation", "contact", "rule", "user", "job"]);

/** Field and alias identifiers: lowercase snake_case only. The compiler also checks them against the catalog. */
export const FieldName = z.string().regex(/^[a-z][a-z0-9_]{0,47}$/, "invalid field name");
const Scalar = z.union([z.string().max(500), z.number().finite(), z.boolean(), z.null()]);
export const FilterValue = z.union([Scalar, z.array(z.union([z.string().max(500), z.number().finite()])).max(200)]);

export const Filter = z.object({ field: FieldName, op: FilterOp, value: FilterValue });
export const Measure = z.object({
  fn: MeasureFn,
  field: FieldName.optional(), // required for sum/avg/min/max/p95/distinct
  as: FieldName.optional(), // result column key (default `${fn}_${field ?? "all"}`)
  /** Conditional aggregate (`count(*) filter (where …)`); required for `rate` (share of rows matching). */
  where: z.array(Filter).max(5).optional(),
});
export const Dimension = z.object({ field: FieldName, granularity: Granularity.optional() });
export const TimeRange = z.object({
  preset: TimePreset,
  from: IsoDate.optional(), // custom only
  to: IsoDate.optional(), // custom only
  compare: CompareMode.nullable().optional(),
});
export const Sort = z.object({ by: FieldName, dir: z.enum(["asc", "desc"]).default("desc") });

export const QUERY_LIMITS = {
  maxMeasures: 8,
  maxDimensions: 3,
  maxFilters: 20,
  maxRows: 5000,
  maxRecordRows: 200,
  defaultRows: 500,
  maxRangeDays: 731,
  maxBuckets: 1500,
  /** `all` presets: bucketed time series are coarsened until they fit (readable charts over years of data). */
  allTimeBuckets: 400,
} as const;

export const WidgetQuery = z.object({
  dataset: DatasetName,
  /** Empty = records mode: one row per item (`select` fields, newest first), for lists, tables and drill-down. */
  measures: z.array(Measure).max(QUERY_LIMITS.maxMeasures).default([]),
  dimensions: z.array(Dimension).max(QUERY_LIMITS.maxDimensions).default([]),
  filters: z.array(Filter).max(QUERY_LIMITS.maxFilters).default([]),
  time: TimeRange.default({ preset: "30d" }),
  /** Records mode only: fields to return (default: the dataset's list fields). `id` is always included. */
  select: z.array(FieldName).max(20).optional(),
  sort: z.array(Sort).max(3).optional(),
  limit: z.number().int().min(1).max(QUERY_LIMITS.maxRows).optional(),
  /** IANA zone for buckets and presets; default the org's timezone (settings.timezone) or UTC. */
  timezone: z.string().max(64).optional(),
});

/** Dashboard-level filter bar, inherited by widgets with `inheritFilters` (time overrides the widget's own). */
export const DashboardFilters = z.object({
  time: TimeRange.default({ preset: "30d" }),
  accounts: z.array(Id).max(50).optional(),
  channels: z.array(Channel).max(10).optional(),
  sentiment: z.array(AiLabels.shape.sentiment.unwrap()).max(3).optional(),
  intent: z.array(AiLabels.shape.intent.unwrap()).max(9).optional(),
  tags: z.array(z.string().max(60)).max(20).optional(),
  posts: z.array(Id).max(100).optional(),
  /** Cross-filters (click a donut slice / bar): applied to every widget whose dataset has the field. */
  extra: z.array(Filter).max(10).optional(),
});

// ---------------------------------------------------------------- query API
export const QueryScope = z.object({ scope: DashboardScope, ref: z.string().max(200).nullable().optional() });
export const AnalyticsQueryRequest = z.object({
  query: WidgetQuery,
  /** Dashboard filters to merge in (send only when the widget inherits them). */
  filters: DashboardFilters.optional(),
  /** Scoped dashboards always constrain their widgets (account → account_id, post → post_id). */
  scope: QueryScope.optional(),
  /** Caching hint: TTL follows the refresh mode. */
  refresh: RefreshMode.optional(),
  /** Bypass the cache read (manual refresh); the result is still cached. */
  noCache: z.boolean().optional(),
});

export const ResultColumn = z.object({
  key: z.string(),
  field: z.string().nullable(), // catalog field (null for measures without a field)
  kind: z.enum(["dimension", "measure", "field"]),
  type: FieldType,
  fn: MeasureFn.optional(),
  granularity: Granularity.optional(),
  entity: EntityKind.optional(),
});
/**
 * Result shape rules (the API compiler and the web mock both follow them):
 * - Column keys: dimension → `field`, or `${field}_${granularity}` when bucketed (`created_at_day`,
 *   `created_at_day_of_week`); measure → `as` ?? `${fn}_${field ?? "all"}`; records mode → the field name.
 * - Time buckets are ISO instants of the bucket start in the query timezone; `hour_of_day` / `day_of_week`
 *   are numbers (0–23, 0 = Sunday).
 * - A bucketed (non-cyclical) first dimension is gap-filled over the whole range: count/sum/rate → 0,
 *   other fns → null; with a second dimension, every bucket × seen value pair exists.
 * - Default order: time dimension ascending, else the first measure descending; records mode: time field desc.
 * - Records mode always includes the dataset's entity id field (`id`, or `post_id` / `account_id`).
 * - Global filters / scope a dataset can't apply are listed in meta.ignoredFilters (e.g. "sentiment", "scope:post").
 * - `all` time: the range starts at the earliest matching row (the org's creation when there is none); a bucketed first
 *   dimension may come back coarser than asked (column `granularity` is the effective one; the key keeps the asked one).
 * - Post-scoped (scope post, or a `posts` filter) queries on the snapshot datasets (posts, post_insights) ignore the time range: a post's
 *   lifetime insights belong to it whenever it was published.
 */
export const CellValue = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export const QueryRange = z.object({ from: IsoDate, to: IsoDate });
export const QueryResult = z.object({
  columns: z.array(ResultColumn),
  rows: z.array(z.record(z.string(), CellValue)),
  /** Same query over the comparison range (rows aligned by position for time series). */
  compare: z.object({ range: QueryRange, rows: z.array(z.record(z.string(), CellValue)) }).nullable(),
  meta: z.object({
    dataset: DatasetName,
    mode: z.enum(["aggregate", "records"]),
    range: QueryRange,
    timezone: z.string(),
    /** Human labels for entity ids in the rows: `{ account_id: { "<uuid>": "@demo.gallery" } }`. */
    labels: z.record(z.string(), z.record(z.string(), z.string())),
    /** Dashboard/cross filters that this dataset cannot apply (no such field). */
    ignoredFilters: z.array(z.string()),
    truncated: z.boolean(),
    rowLimit: z.number(),
    cached: z.boolean(),
    durationMs: z.number(),
    generatedAt: IsoDate,
  }),
});

// ---------------------------------------------------------------- catalog
export const NumberFormat = z.enum(["number", "compact", "percent", "currency", "duration"]);
export const WidgetOptions = z.object({
  /** Palette slots 1–6 (--chart-N), in series order. */
  colors: z.array(z.number().int().min(1).max(6)).max(12).optional(),
  stacked: z.boolean().optional(),
  showLegend: z.boolean().optional(),
  showCompare: z.boolean().optional(),
  threshold: z.number().finite().nullable().optional(),
  goal: z.number().finite().nullable().optional(),
  format: NumberFormat.optional(),
  /** Markdown body of a text widget. */
  text: z.string().max(5000).optional(),
  /** Rows shown by lists/tables/top-N charts. */
  topN: z.number().int().min(1).max(200).optional(),
});
export const CatalogField = z.object({
  name: FieldName,
  type: FieldType,
  label: z.string(),
  enumValues: z.array(z.string()).optional(),
  entity: EntityKind.optional(),
  dimension: z.boolean(),
  measures: z.array(MeasureFn), // allowed measure fns over this field (count is always allowed without a field)
  ops: z.array(FilterOp),
  /** Shown by default in records mode. */
  list: z.boolean().optional(),
});
export const CatalogDataset = z.object({
  name: DatasetName,
  label: z.string(),
  description: z.string(),
  timeField: FieldName,
  entity: EntityKind.optional(),
  fields: z.array(CatalogField),
  /** Which DashboardFilters keys apply here (e.g. accounts → account_id). */
  globalFilters: z.array(z.enum(["accounts", "channels", "sentiment", "intent", "tags", "posts"])),
});
export const WidgetSize = z.object({ w: z.number().int(), h: z.number().int() });
export const WidgetTemplate = z.object({
  key: z.string(),
  title: z.string(),
  description: z.string(),
  category: z.enum(["engagement", "audience", "inbox", "automation", "content", "operations"]),
  viz: VizType,
  query: WidgetQuery.nullable(),
  options: WidgetOptions,
  size: WidgetSize,
});
export const AnalyticsCatalog = z.object({
  datasets: z.array(CatalogDataset),
  limits: z.object({ maxMeasures: z.number(), maxDimensions: z.number(), maxFilters: z.number(), maxRows: z.number(), maxRecordRows: z.number(), maxRangeDays: z.number() }),
  widgetTemplates: z.array(WidgetTemplate),
  dashboardTemplates: z.array(z.object({ key: TemplateKey, scope: DashboardScope, name: z.string(), widgets: z.array(z.string()) })),
});

// ---------------------------------------------------------------- widgets & dashboards
export const GRID_COLUMNS = 12;
export const WidgetLayout = z.object({
  x: z.number().int().min(0).max(GRID_COLUMNS - 1),
  y: z.number().int().min(0).max(10_000),
  w: z.number().int().min(1).max(GRID_COLUMNS),
  h: z.number().int().min(1).max(40),
});
export const Widget = z.object({
  id: Id,
  dashboardId: Id,
  title: z.string().min(1).max(120),
  subtitle: z.string().max(200).nullable(),
  viz: VizType,
  query: WidgetQuery.nullable(), // null only for text widgets
  options: WidgetOptions,
  layout: WidgetLayout,
  refresh: RefreshMode,
  inheritFilters: z.boolean(),
  version: z.number().int(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
export const WidgetInput = z.object({
  title: z.string().min(1).max(120),
  subtitle: z.string().max(200).nullable().optional(),
  viz: VizType,
  query: WidgetQuery.nullable(),
  options: WidgetOptions.default({}),
  layout: WidgetLayout.optional(), // default: appended at the bottom
  refresh: RefreshMode.default("live"),
  inheritFilters: z.boolean().default(true),
});
export const WidgetPatch = WidgetInput.partial().extend({ version: z.number().int().optional() });

export const DashboardSummary = z.object({
  id: Id,
  orgId: Id,
  ownerId: Id.nullable(),
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullable(),
  scope: DashboardScope,
  scopeRef: z.string().nullable(),
  template: TemplateKey.nullable(),
  filters: DashboardFilters,
  shared: DashboardSharing,
  isDefault: z.boolean(),
  /** Whether the caller may edit (role + ownership); the UI hides edit mode otherwise. */
  canEdit: z.boolean(),
  widgetCount: z.number().int(),
  version: z.number().int(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
export const Dashboard = DashboardSummary.extend({ widgets: z.array(Widget) });

export const CreateDashboardRequest = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullable().optional(),
  scope: DashboardScope.default("org"),
  scopeRef: z.string().max(200).nullable().optional(),
  /** Start from a template's widgets; omitted = empty dashboard. */
  template: TemplateKey.nullable().optional(),
  filters: DashboardFilters.optional(),
  shared: DashboardSharing.default("private"),
  isDefault: z.boolean().default(false),
});
export const UpdateDashboardRequest = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(500).nullable().optional(),
  filters: DashboardFilters.optional(),
  shared: DashboardSharing.optional(),
  isDefault: z.boolean().optional(),
  /** Optimistic concurrency: 409 when the dashboard changed since this version. */
  version: z.number().int().optional(),
});
export const LayoutUpdateRequest = z.object({ items: z.array(z.object({ id: Id, layout: WidgetLayout })).min(1).max(200) });
export const PinRequest = z.object({
  /** Target dashboard; omit and pass `newDashboard` to create one. */
  dashboardId: Id.optional(),
  newDashboard: z.object({ name: z.string().min(1).max(120), shared: DashboardSharing.default("private") }).optional(),
  widget: WidgetInput,
});
export const PinResult = z.object({ dashboard: DashboardSummary, widget: Widget });

export const DashboardListQuery = z.object({ scope: DashboardScope.optional(), scopeRef: z.string().max(200).optional() });
export const DefaultDashboardQuery = z.object({ scope: DashboardScope.default("org"), scopeRef: z.string().max(200).optional() });

// ---------------------------------------------------------------- catalog metadata (shared by API compiler, mock and builder UI)
type FieldSpec = z.input<typeof CatalogField>;
const SENTIMENTS = ["positive", "neutral", "negative"];
const INTENTS = ["question", "price", "purchase", "collab", "complaint", "praise", "spam", "hate", "other"];
const CHANNELS = Channel.options as unknown as string[];
const ID_OPS: FieldSpec["ops"] = ["eq", "neq", "in", "nin"];
const ENUM_OPS: FieldSpec["ops"] = ["eq", "neq", "in", "nin"];
const STR_OPS: FieldSpec["ops"] = ["eq", "neq", "in", "nin", "contains"];
const NUM_OPS: FieldSpec["ops"] = ["eq", "neq", "gt", "gte", "lt", "lte"];
const TIME_OPS: FieldSpec["ops"] = ["gte", "lte", "gt", "lt"];
const NUM_FNS: FieldSpec["measures"] = ["sum", "avg", "min", "max", "p95"];

const f = {
  id: (name: string, label: string, entity: FieldSpec["entity"], extra: Partial<FieldSpec> = {}): FieldSpec => ({ name, type: "id", label, entity, dimension: true, measures: ["distinct"], ops: ID_OPS, ...extra }),
  en: (name: string, label: string, values: string[], extra: Partial<FieldSpec> = {}): FieldSpec => ({ name, type: "enum", label, enumValues: values, dimension: true, measures: ["distinct"], ops: ENUM_OPS, ...extra }),
  str: (name: string, label: string, extra: Partial<FieldSpec> = {}): FieldSpec => ({ name, type: "string", label, dimension: true, measures: ["distinct"], ops: STR_OPS, ...extra }),
  /** Free text: `contains` (substring) and `semantic` (meaning, via the SemanticIndex: resolved to an id filter before compiling). */
  text: (name: string, label: string): FieldSpec => ({ name, type: "string", label, dimension: false, measures: [], ops: ["contains", "semantic"], list: true }),
  num: (name: string, label: string, extra: Partial<FieldSpec> = {}): FieldSpec => ({ name, type: "number", label, dimension: false, measures: NUM_FNS, ops: NUM_OPS, ...extra }),
  bool: (name: string, label: string, extra: Partial<FieldSpec> = {}): FieldSpec => ({ name, type: "boolean", label, dimension: true, measures: [], ops: ["eq"], ...extra }),
  time: (name: string, label: string, extra: Partial<FieldSpec> = {}): FieldSpec => ({ name, type: "time", label, dimension: true, measures: ["min", "max"], ops: TIME_OPS, ...extra }),
};

const LIST = { list: true } as const;
/**
 * ALP-83 (measurable tags): built-in flags, custom tags and custom AI categories (entity_flags) as a dimension and a
 * filter. Many-to-many: grouped by `tag`, an item is counted once per tag it carries (never once per other join row);
 * as a filter it is an EXISTS, so it never multiplies rows. `tagged_by_rule` = the rule that added the flag
 * (entity_flags.added_by for added_by_type = rule); as a dimension it pairs with `tag` the same way.
 */
const TAG_FIELD: FieldSpec = { ...f.str("tag", "Tag / flag / category"), ops: ["eq", "neq", "in", "nin"] };
const TAGGED_BY_RULE_FIELD: FieldSpec = f.id("tagged_by_rule", "Tagged by rule", "rule");
export const DATASETS = [
  {
    name: "comments", label: "Comments", description: "Comments on your posts, with AI labels.", timeField: "created_at", entity: "comment",
    globalFilters: ["accounts", "channels", "sentiment", "intent", "posts", "tags"],
    fields: [
      f.id("id", "Comment", "comment", LIST),
      f.text("text", "Text"),
      f.str("author", "Author", LIST),
      f.id("contact_id", "Contact", "contact"),
      f.id("account_id", "Account", "account", LIST),
      f.id("post_id", "Post", "post"),
      f.id("publication_id", "Publication", "publication"),
      f.en("channel", "Channel", CHANNELS),
      f.en("status", "Status", ["visible", "hidden", "deleted"], LIST),
      f.en("inbox_state", "Inbox state", ["open", "done", "snoozed"]),
      f.en("sentiment", "Sentiment", SENTIMENTS, LIST),
      f.en("intent", "Intent", INTENTS, LIST),
      f.str("language", "Language"),
      f.num("toxicity", "Toxicity"),
      f.num("confidence", "AI confidence"),
      f.num("replies_count", "Replies"),
      f.bool("replied", "Answered by the account"),
      f.num("response_minutes", "Response time (min)"),
      f.bool("is_reply", "Is a reply"),
      f.bool("by_account", "Written by the account"),
      f.bool("assigned", "Assigned"),
      f.id("assignee_id", "Assignee", "user"),
      TAG_FIELD,
      TAGGED_BY_RULE_FIELD,
      f.time("created_at", "Created", LIST),
    ],
  },
  {
    name: "messages", label: "Direct messages", description: "Every DM sent and received.", timeField: "sent_at",
    globalFilters: ["accounts", "channels", "tags"],
    fields: [
      f.id("id", "Message", undefined),
      f.id("conversation_id", "Conversation", "conversation", LIST),
      f.id("account_id", "Account", "account"),
      f.en("channel", "Channel", CHANNELS),
      f.en("direction", "Direction", ["in", "out"], LIST),
      f.en("sent_by", "Sent by", ["contact", "user", "automation", "ai"], LIST),
      f.en("status", "Status", ["received", "sending", "sent", "delivered", "read", "failed"]),
      f.bool("has_attachment", "Has attachment"),
      f.text("text", "Text"),
      TAG_FIELD, // the conversation's flags / tags
      f.time("sent_at", "Sent", LIST),
    ],
  },
  {
    name: "conversations", label: "Conversations", description: "DM threads with contacts.", timeField: "last_message_at", entity: "conversation",
    globalFilters: ["accounts", "channels", "sentiment", "intent", "tags"],
    fields: [
      f.id("id", "Conversation", "conversation", LIST),
      f.id("account_id", "Account", "account", LIST),
      f.id("contact_id", "Contact", "contact", LIST),
      f.en("channel", "Channel", CHANNELS),
      f.en("inbox_state", "Inbox state", ["open", "done", "snoozed"], LIST),
      f.en("sentiment", "Sentiment", SENTIMENTS, LIST),
      f.en("intent", "Intent", INTENTS, LIST),
      f.num("unread", "Unread", LIST),
      f.bool("ai_agent", "AI agent on"),
      f.bool("window_open", "24h window open"),
      f.bool("assigned", "Assigned"),
      f.id("assignee_id", "Assignee", "user"),
      TAG_FIELD,
      TAGGED_BY_RULE_FIELD,
      f.time("last_message_at", "Last message", LIST),
    ],
  },
  {
    name: "posts", label: "Posts", description: "Published and scheduled posts per account.", timeField: "published_at", entity: "post",
    globalFilters: ["accounts", "channels", "posts"],
    fields: [
      f.id("id", "Publication", "publication"),
      f.id("post_id", "Post", "post", LIST),
      f.id("account_id", "Account", "account", LIST),
      f.en("channel", "Channel", CHANNELS),
      f.en("type", "Type", ["image", "video", "carousel", "reel", "story", "text"], LIST),
      f.en("status", "Status", ["draft", "scheduled", "publishing", "published", "failed", "cancelled"], LIST),
      f.text("caption", "Caption"),
      { ...f.str("permalink", "Link"), dimension: false, measures: [], ops: [], list: true },
      { ...f.str("media_url", "Media"), dimension: false, measures: [], ops: [], list: true },
      f.bool("imported", "Imported"),
      f.num("comments_count", "Comments", LIST),
      f.num("likes_count", "Likes", LIST),
      f.time("published_at", "Published", LIST),
      f.time("scheduled_at", "Scheduled"),
    ],
  },
  {
    name: "post_insights", label: "Post insights", description: "Lifetime reach, views, saves and shares per post.", timeField: "published_at", entity: "post",
    globalFilters: ["accounts", "channels", "posts"],
    fields: [
      f.id("post_id", "Post", "post", LIST),
      f.id("publication_id", "Publication", "publication"),
      f.id("account_id", "Account", "account"),
      f.en("channel", "Channel", CHANNELS),
      f.en("type", "Type", ["image", "video", "carousel", "reel", "story", "text"]),
      f.num("reach", "Reach", LIST),
      f.num("views", "Views", LIST),
      f.num("saves", "Saves", LIST),
      f.num("shares", "Shares", LIST),
      f.num("likes", "Likes", LIST),
      f.num("comments", "Comments", LIST),
      f.num("interactions", "Interactions"),
      f.num("engagement_rate", "Engagement rate"),
      f.time("published_at", "Published", LIST),
    ],
  },
  {
    name: "post_metrics", label: "Post metrics over time", description: "Daily snapshots of each post's reach, views, likes, saves and comments.", timeField: "day", entity: "post",
    globalFilters: ["accounts", "posts"],
    fields: [
      f.id("post_id", "Post", "post", LIST),
      f.id("publication_id", "Publication", "publication"),
      f.id("account_id", "Account", "account"),
      f.num("reach", "Reach", LIST),
      f.num("views", "Views", LIST),
      f.num("likes", "Likes", LIST),
      f.num("saves", "Saves", LIST),
      f.num("shares", "Shares"),
      f.num("comments", "Comments", LIST),
      f.time("day", "Day", LIST),
    ],
  },
  {
    name: "account_insights", label: "Account insights", description: "Daily account metrics (reach, views, followers…).", timeField: "day",
    globalFilters: ["accounts"],
    fields: [
      f.id("account_id", "Account", "account", LIST),
      f.num("reach", "Reach", LIST),
      f.num("views", "Views", LIST),
      f.num("accounts_engaged", "Accounts engaged"),
      f.num("total_interactions", "Interactions", LIST),
      f.num("likes", "Likes"),
      f.num("comments", "Comments"),
      f.num("shares", "Shares"),
      f.num("saves", "Saves"),
      f.num("profile_views", "Profile views"),
      f.num("followers", "Followers", LIST),
      f.time("day", "Day", LIST),
    ],
  },
  {
    name: "automation_runs", label: "Automation runs", description: "Every rule evaluation and the action taken.", timeField: "created_at",
    globalFilters: ["accounts", "sentiment", "intent", "posts"],
    fields: [
      f.id("id", "Run", undefined),
      f.id("rule_id", "Rule", "rule", LIST),
      f.id("account_id", "Account", "account"),
      f.id("post_id", "Post", "post"),
      f.id("comment_id", "Comment", "comment"),
      f.bool("matched", "Matched", LIST),
      f.str("action", "Action", LIST),
      f.en("result", "Result", ["executed", "pending_approval", "skipped", "failed", "suggested"], LIST),
      f.en("sentiment", "Sentiment", SENTIMENTS),
      f.en("intent", "Intent", INTENTS),
      f.time("created_at", "Created", LIST),
    ],
  },
  {
    name: "ai_usage", label: "AI usage", description: "AI tokens and actions metered per org.", timeField: "at",
    globalFilters: [],
    fields: [
      f.str("kind", "Kind", LIST),
      f.str("model", "Model", LIST),
      f.str("feature", "Feature", LIST),
      f.num("qty", "Quantity", LIST),
      f.time("at", "At", LIST),
    ],
  },
  {
    name: "jobs", label: "Jobs", description: "Long operations: syncs, bulk actions, publishes.", timeField: "created_at", entity: "job",
    globalFilters: [],
    fields: [
      f.id("id", "Job", "job", LIST),
      f.str("kind", "Kind", LIST),
      f.en("status", "Status", ["queued", "running", "paused", "succeeded", "failed", "cancelled"], LIST),
      f.num("done", "Done", LIST),
      f.num("total", "Total"),
      f.num("failed", "Failed", LIST),
      f.num("duration_sec", "Duration (s)"),
      f.id("created_by", "Started by", "user"),
      f.time("created_at", "Created", LIST),
      f.time("finished_at", "Finished"),
    ],
  },
] as const satisfies readonly z.input<typeof CatalogDataset>[];

/** Datasets each live event touches (API debounce → `dataset.changed`; also used by the mock). */
export const EVENT_DATASETS: Record<string, readonly z.infer<typeof DatasetName>[]> = {
  "inbox.item": ["comments", "conversations", "messages"],
  "comment.updated": ["comments"],
  "message.created": ["messages", "conversations"],
  "message.updated": ["messages", "conversations"],
  "approval.created": ["automation_runs"],
  "operation.updated": ["jobs"],
  "publication.updated": ["posts", "post_insights", "post_metrics"],
};

// ---------------------------------------------------------------- templates (widgets are queries)
type WT = z.input<typeof WidgetTemplate>;
const q = (x: z.input<typeof WidgetQuery>) => x;
export const WIDGET_TEMPLATES: WT[] = [
  { key: "kpi_comments", category: "engagement", title: "Comments", description: "Comments received, vs the previous period.", viz: "kpi", size: { w: 3, h: 3 },
    query: q({ dataset: "comments", measures: [{ fn: "count", as: "comments" }], filters: [{ field: "by_account", op: "eq", value: false }], time: { preset: "30d", compare: "previous_period" } }), options: { format: "compact", showCompare: true } },
  { key: "kpi_negative_rate", category: "engagement", title: "Negative share", description: "Share of comments with negative sentiment.", viz: "kpi", size: { w: 3, h: 3 },
    query: q({ dataset: "comments", measures: [{ fn: "rate", as: "negative_rate", where: [{ field: "sentiment", op: "eq", value: "negative" }] }], filters: [{ field: "by_account", op: "eq", value: false }], time: { preset: "30d", compare: "previous_period" } }), options: { format: "percent", showCompare: true } },
  { key: "kpi_post_reach", category: "content", title: "Post reach", description: "Lifetime reach of the posts in range.", viz: "kpi", size: { w: 3, h: 3 },
    query: q({ dataset: "post_insights", measures: [{ fn: "sum", field: "reach", as: "reach" }], time: { preset: "90d" } }), options: { format: "compact" } },
  { key: "kpi_reach", category: "audience", title: "Reach", description: "Accounts reached (daily account insights).", viz: "kpi", size: { w: 3, h: 3 },
    query: q({ dataset: "account_insights", measures: [{ fn: "sum", field: "reach", as: "reach" }], time: { preset: "30d", compare: "previous_period" } }), options: { format: "compact", showCompare: true } },
  { key: "kpi_automation", category: "automation", title: "Handled by automation", description: "Rule matches that ran an action.", viz: "kpi", size: { w: 3, h: 3 },
    query: q({ dataset: "automation_runs", measures: [{ fn: "count", as: "handled", where: [{ field: "result", op: "eq", value: "executed" }] }], time: { preset: "30d", compare: "previous_period" } }), options: { format: "compact", showCompare: true } },
  { key: "comments_over_time", category: "engagement", title: "Comments over time", description: "Daily comments by sentiment.", viz: "stacked_bar", size: { w: 8, h: 6 },
    query: q({ dataset: "comments", measures: [{ fn: "count", as: "comments" }], dimensions: [{ field: "created_at", granularity: "day" }, { field: "sentiment" }], filters: [{ field: "by_account", op: "eq", value: false }], time: { preset: "30d" } }), options: { showLegend: true, stacked: true } },
  { key: "sentiment_donut", category: "engagement", title: "Sentiment", description: "Comment sentiment split. Click a slice to filter the dashboard.", viz: "donut", size: { w: 4, h: 6 },
    query: q({ dataset: "comments", measures: [{ fn: "count", as: "comments" }], dimensions: [{ field: "sentiment" }], filters: [{ field: "by_account", op: "eq", value: false }], time: { preset: "30d" } }), options: { showLegend: true } },
  { key: "intent_bar", category: "inbox", title: "What people ask", description: "Comments by AI intent.", viz: "bar", size: { w: 6, h: 6 },
    query: q({ dataset: "comments", measures: [{ fn: "count", as: "comments" }], dimensions: [{ field: "intent" }], filters: [{ field: "by_account", op: "eq", value: false }], sort: [{ by: "comments", dir: "desc" }], time: { preset: "30d" } }), options: { topN: 9 } },
  { key: "reach_trend", category: "audience", title: "Reach & views", description: "Daily reach and views with the previous period.", viz: "area", size: { w: 8, h: 6 },
    query: q({ dataset: "account_insights", measures: [{ fn: "sum", field: "reach", as: "reach" }, { fn: "sum", field: "views", as: "views" }], dimensions: [{ field: "day", granularity: "day" }], time: { preset: "30d", compare: "previous_period" } }), options: { showLegend: true, showCompare: true, format: "compact" } },
  { key: "activity_heatmap", category: "audience", title: "When people comment", description: "Comments by weekday and hour.", viz: "heatmap", size: { w: 6, h: 6 },
    query: q({ dataset: "comments", measures: [{ fn: "count", as: "comments" }], dimensions: [{ field: "created_at", granularity: "day_of_week" }, { field: "created_at", granularity: "hour_of_day" }], filters: [{ field: "by_account", op: "eq", value: false }], time: { preset: "90d" } }), options: {} },
  { key: "latest_comments", category: "inbox", title: "Latest comments", description: "Newest comments, live.", viz: "comments", size: { w: 4, h: 7 },
    query: q({ dataset: "comments", measures: [], filters: [{ field: "by_account", op: "eq", value: false }], select: ["id", "text", "author", "sentiment", "intent", "status", "created_at", "post_id"], limit: 20, time: { preset: "7d" } }), options: { topN: 20 } },
  { key: "negative_comments", category: "inbox", title: "Negative comments", description: "Comments that need care.", viz: "comments", size: { w: 4, h: 7 },
    query: q({ dataset: "comments", measures: [], filters: [{ field: "by_account", op: "eq", value: false }, { field: "sentiment", op: "eq", value: "negative" }], select: ["id", "text", "author", "sentiment", "intent", "status", "created_at", "post_id"], limit: 20, time: { preset: "30d" } }), options: { topN: 20 } },
  { key: "top_posts", category: "content", title: "Top posts", description: "Posts ranked by reach.", viz: "posts", size: { w: 8, h: 7 },
    query: q({ dataset: "post_insights", measures: [], select: ["post_id", "reach", "views", "likes", "comments", "saves", "published_at"], sort: [{ by: "reach", dir: "desc" }], limit: 6, time: { preset: "90d" } }), options: { topN: 6 } },
  { key: "post_metrics_trend", category: "content", title: "Post performance over time", description: "Daily reach, views, likes, saves and comments.", viz: "line", size: { w: 8, h: 6 },
    query: q({ dataset: "post_metrics", measures: [{ fn: "max", field: "reach", as: "reach" }, { fn: "max", field: "views", as: "views" }, { fn: "max", field: "likes", as: "likes" }, { fn: "max", field: "saves", as: "saves" }, { fn: "max", field: "comments", as: "comments" }], dimensions: [{ field: "day", granularity: "day" }], time: { preset: "30d" } }), options: { showLegend: true, format: "compact" } },
  { key: "top_commenters", category: "audience", title: "Top commenters", description: "Who comments the most.", viz: "bar", size: { w: 4, h: 6 },
    query: q({ dataset: "comments", measures: [{ fn: "count", as: "comments" }], dimensions: [{ field: "author" }], filters: [{ field: "by_account", op: "eq", value: false }], sort: [{ by: "comments", dir: "desc" }], limit: 8, time: { preset: "30d" } }), options: { topN: 8 } },
  { key: "kpi_reply_rate", category: "inbox", title: "Reply rate", description: "Share of comments the account answered.", viz: "kpi", size: { w: 3, h: 3 },
    query: q({ dataset: "comments", measures: [{ fn: "rate", as: "reply_rate", where: [{ field: "replied", op: "eq", value: true }] }], filters: [{ field: "by_account", op: "eq", value: false }, { field: "is_reply", op: "eq", value: false }], time: { preset: "30d", compare: "previous_period" } }), options: { format: "percent", showCompare: true } },
  { key: "kpi_response_time", category: "inbox", title: "Response time", description: "Average minutes until the account replied.", viz: "kpi", size: { w: 3, h: 3 },
    query: q({ dataset: "comments", measures: [{ fn: "avg", field: "response_minutes", as: "response_minutes" }], filters: [{ field: "by_account", op: "eq", value: false }], time: { preset: "30d", compare: "previous_period" } }), options: { format: "duration", showCompare: true } },
  { key: "post_table", category: "content", title: "Post performance", description: "Reach, views, saves and engagement per post.", viz: "table", size: { w: 12, h: 7 },
    query: q({ dataset: "post_insights", measures: [{ fn: "sum", field: "reach", as: "reach" }, { fn: "sum", field: "views", as: "views" }, { fn: "sum", field: "saves", as: "saves" }, { fn: "avg", field: "engagement_rate", as: "engagement_rate" }], dimensions: [{ field: "post_id" }], sort: [{ by: "reach", dir: "desc" }], limit: 20, time: { preset: "90d" } }), options: {} },
  { key: "automation_funnel", category: "automation", title: "Automation funnel", description: "Events → matched → executed.", viz: "funnel", size: { w: 4, h: 6 },
    query: q({ dataset: "automation_runs", measures: [{ fn: "count", as: "evaluated" }, { fn: "count", as: "matched", where: [{ field: "matched", op: "eq", value: true }] }, { fn: "count", as: "executed", where: [{ field: "result", op: "eq", value: "executed" }] }], time: { preset: "30d" } }), options: {} },
  { key: "automation_by_rule", category: "automation", title: "Actions by rule", description: "Which rules do the work.", viz: "bar", size: { w: 6, h: 6 },
    query: q({ dataset: "automation_runs", measures: [{ fn: "count", as: "runs", where: [{ field: "matched", op: "eq", value: true }] }], dimensions: [{ field: "rule_id" }], sort: [{ by: "runs", dir: "desc" }], time: { preset: "30d" } }), options: { topN: 8 } },
  { key: "dm_volume", category: "inbox", title: "DMs in vs out", description: "Messages received and sent per day.", viz: "line", size: { w: 6, h: 6 },
    query: q({ dataset: "messages", measures: [{ fn: "count", as: "messages" }], dimensions: [{ field: "sent_at", granularity: "day" }, { field: "direction" }], time: { preset: "30d" } }), options: { showLegend: true } },
  { key: "open_conversations", category: "inbox", title: "Open conversations", description: "DM threads waiting on you.", viz: "kpi", size: { w: 3, h: 3 },
    query: q({ dataset: "conversations", measures: [{ fn: "count", as: "open" }], filters: [{ field: "inbox_state", op: "eq", value: "open" }], time: { preset: "90d" } }), options: { format: "number" } },
  { key: "followers_trend", category: "audience", title: "Followers", description: "Follower count over time.", viz: "line", size: { w: 6, h: 6 },
    query: q({ dataset: "account_insights", measures: [{ fn: "max", field: "followers", as: "followers" }], dimensions: [{ field: "day", granularity: "day" }], time: { preset: "90d" } }), options: { format: "compact" } },
  { key: "jobs_status", category: "operations", title: "Jobs", description: "Long operations by status.", viz: "donut", size: { w: 4, h: 6 },
    query: q({ dataset: "jobs", measures: [{ fn: "count", as: "jobs" }], dimensions: [{ field: "status" }], time: { preset: "30d" } }), options: { showLegend: true } },
  { key: "ai_usage_trend", category: "operations", title: "AI usage", description: "AI tokens per day.", viz: "bar", size: { w: 6, h: 6 },
    query: q({ dataset: "ai_usage", measures: [{ fn: "sum", field: "qty", as: "tokens" }], dimensions: [{ field: "at", granularity: "day" }], time: { preset: "30d" } }), options: { format: "compact" } },
  // ALP-83: measurable tags (built-in flags + custom tags + AI categories)
  { key: "comments_by_tag", category: "engagement", title: "Comments by tag", description: "Comments per flag, tag and AI category. Click a bar to filter.", viz: "bar", size: { w: 4, h: 6 },
    query: q({ dataset: "comments", measures: [{ fn: "count", as: "comments" }], dimensions: [{ field: "tag" }], filters: [{ field: "by_account", op: "eq", value: false }], sort: [{ by: "comments", dir: "desc" }], limit: 12, time: { preset: "30d" } }), options: { topN: 12 } },
  { key: "tag_trend", category: "engagement", title: "Tag trend over time", description: "Tagged comments per day, by tag.", viz: "stacked_bar", size: { w: 8, h: 6 },
    query: q({ dataset: "comments", measures: [{ fn: "count", as: "comments" }], dimensions: [{ field: "created_at", granularity: "day" }, { field: "tag" }], filters: [{ field: "by_account", op: "eq", value: false }], time: { preset: "30d" } }), options: { showLegend: true, stacked: true } },
  { key: "top_posts_by_tag", category: "content", title: "Top posts by tag", description: "Which posts collect which tags.", viz: "table", size: { w: 8, h: 7 },
    query: q({ dataset: "comments", measures: [{ fn: "count", as: "comments" }], dimensions: [{ field: "post_id" }, { field: "tag" }], filters: [{ field: "by_account", op: "eq", value: false }], sort: [{ by: "comments", dir: "desc" }], limit: 20, time: { preset: "30d" } }), options: { topN: 20 } },
  { key: "tagged_by_rule", category: "automation", title: "Tagged by rule", description: "Comments each rule tagged.", viz: "bar", size: { w: 6, h: 6 },
    query: q({ dataset: "comments", measures: [{ fn: "count", as: "comments" }], dimensions: [{ field: "tagged_by_rule" }], filters: [{ field: "tagged_by_rule", op: "neq", value: null }], sort: [{ by: "comments", dir: "desc" }], limit: 10, time: { preset: "30d" } }), options: { topN: 10 } },
  { key: "note", category: "operations", title: "Note", description: "A text block for context or goals.", viz: "text", size: { w: 4, h: 3 },
    query: null, options: { text: "**Goal:** reply to every price question within 1 hour." } },
];

/** Dashboard templates: an ordered list of widget template keys laid out left-to-right, top-to-bottom. */
export const DASHBOARD_TEMPLATES: { key: z.infer<typeof TemplateKey>; scope: z.infer<typeof DashboardScope>; name: string; widgets: string[] }[] = [
  { key: "home", scope: "org", name: "Home", widgets: ["kpi_reach", "kpi_comments", "kpi_negative_rate", "kpi_automation", "reach_trend", "sentiment_donut", "comments_over_time", "latest_comments", "intent_bar", "automation_by_rule", "top_posts", "automation_funnel"] },
  { key: "account", scope: "account", name: "Account", widgets: ["kpi_reach", "kpi_comments", "kpi_negative_rate", "open_conversations", "reach_trend", "sentiment_donut", "activity_heatmap", "followers_trend", "top_posts", "latest_comments", "dm_volume", "intent_bar"] },
  { key: "post", scope: "post", name: "Post", widgets: ["kpi_post_reach", "kpi_comments", "kpi_reply_rate", "kpi_response_time", "post_metrics_trend", "sentiment_donut", "comments_by_tag", "comments_over_time", "top_commenters", "intent_bar", "automation_by_rule", "latest_comments", "kpi_negative_rate", "kpi_automation"] },
  { key: "campaign", scope: "campaign", name: "Campaign", widgets: ["kpi_post_reach", "kpi_comments", "kpi_negative_rate", "kpi_automation", "post_table", "comments_over_time", "sentiment_donut", "top_posts", "negative_comments"] },
];

/** Pack template widgets into the 12-column grid (row-major, wrapping when a row is full). */
export function layoutTemplate(keys: string[]): { key: string; layout: z.infer<typeof WidgetLayout> }[] {
  let x = 0;
  let y = 0;
  let rowH = 0;
  const out: { key: string; layout: z.infer<typeof WidgetLayout> }[] = [];
  for (const key of keys) {
    const t = WIDGET_TEMPLATES.find((w) => w.key === key);
    if (!t) continue;
    const { w, h } = t.size;
    if (x + w > GRID_COLUMNS) {
      x = 0;
      y += rowH;
      rowH = 0;
    }
    out.push({ key, layout: { x, y, w, h } });
    x += w;
    rowH = Math.max(rowH, h);
  }
  return out;
}

export type DatasetName = z.infer<typeof DatasetName>;
export type MeasureFn = z.infer<typeof MeasureFn>;
export type Granularity = z.infer<typeof Granularity>;
export type FilterOp = z.infer<typeof FilterOp>;
export type FieldType = z.infer<typeof FieldType>;
export type TimePreset = z.infer<typeof TimePreset>;
export type VizType = z.infer<typeof VizType>;
export type RefreshMode = z.infer<typeof RefreshMode>;
export type DashboardScope = z.infer<typeof DashboardScope>;
export type Filter = z.infer<typeof Filter>;
export type Measure = z.infer<typeof Measure>;
export type Dimension = z.infer<typeof Dimension>;
export type TimeRange = z.infer<typeof TimeRange>;
export type WidgetQuery = z.infer<typeof WidgetQuery>;
export type WidgetQueryInput = z.input<typeof WidgetQuery>;
export type DashboardFilters = z.infer<typeof DashboardFilters>;
export type AnalyticsQueryRequest = z.infer<typeof AnalyticsQueryRequest>;
export type QueryResult = z.infer<typeof QueryResult>;
export type ResultColumn = z.infer<typeof ResultColumn>;
export type CatalogField = z.infer<typeof CatalogField>;
export type CatalogDataset = z.infer<typeof CatalogDataset>;
export type AnalyticsCatalog = z.infer<typeof AnalyticsCatalog>;
export type WidgetTemplate = z.infer<typeof WidgetTemplate>;
export type WidgetLayout = z.infer<typeof WidgetLayout>;
export type WidgetOptions = z.infer<typeof WidgetOptions>;
export type Widget = z.infer<typeof Widget>;
export type WidgetInput = z.infer<typeof WidgetInput>;
export type WidgetPatch = z.infer<typeof WidgetPatch>;
export type Dashboard = z.infer<typeof Dashboard>;
export type DashboardSummary = z.infer<typeof DashboardSummary>;
export type PinRequest = z.infer<typeof PinRequest>;

// ---------------------------------------------------------------- routes (spread into `routes`)
const route = <P extends z.ZodTypeAny, Q extends z.ZodTypeAny, B extends z.ZodTypeAny, R extends z.ZodTypeAny>(d: RouteDef<P, Q, B, R>) => d;
const OrgP = z.object({ orgId: z.string().uuid() });
const DashP = OrgP.extend({ dashboardId: z.string().uuid() });
const WidgetP = DashP.extend({ widgetId: z.string().uuid() });
const OkR = z.object({ ok: z.literal(true) });
const EmptyB = z.object({});

export const dashboardRoutes = {
  // semantic layer
  analyticsCatalog: route({ method: "GET", path: "/v1/orgs/:orgId/analytics/catalog", params: OrgP, response: AnalyticsCatalog, summary: "Datasets, fields, allowed measures/dimensions/filters, widget + dashboard templates" }),
  analyticsQuery: route({ method: "POST", path: "/v1/orgs/:orgId/analytics/query", params: OrgP, body: AnalyticsQueryRequest, response: QueryResult, summary: "Run a WidgetQuery (compiled to org-scoped parameterized SQL; cached; read-only, viewer allowed)" }),

  // dashboards
  dashboards: route({ method: "GET", path: "/v1/orgs/:orgId/dashboards", params: OrgP, query: DashboardListQuery, response: z.array(DashboardSummary), summary: "Dashboards the caller can see (own private + team)" }),
  defaultDashboard: route({ method: "GET", path: "/v1/orgs/:orgId/dashboards/default", params: OrgP, query: DefaultDashboardQuery, response: Dashboard, summary: "Default dashboard of a scope (created from its template on first use)" }),
  dashboard: route({ method: "GET", path: "/v1/orgs/:orgId/dashboards/:dashboardId", params: DashP, response: Dashboard, summary: "Dashboard with widgets" }),
  createDashboard: route({ method: "POST", path: "/v1/orgs/:orgId/dashboards", params: OrgP, body: CreateDashboardRequest, response: Dashboard, status: 201, summary: "Create a dashboard (empty or from a template)" }),
  updateDashboard: route({ method: "PATCH", path: "/v1/orgs/:orgId/dashboards/:dashboardId", params: DashP, body: UpdateDashboardRequest, response: Dashboard, summary: "Rename, filters, sharing, default" }),
  deleteDashboard: route({ method: "DELETE", path: "/v1/orgs/:orgId/dashboards/:dashboardId", params: DashP, response: OkR, summary: "Delete a dashboard" }),
  resetDashboard: route({ method: "POST", path: "/v1/orgs/:orgId/dashboards/:dashboardId/reset", params: DashP, body: EmptyB, response: Dashboard, summary: "Reset widgets and filters to the dashboard's template" }),
  updateLayout: route({ method: "PUT", path: "/v1/orgs/:orgId/dashboards/:dashboardId/layout", params: DashP, body: LayoutUpdateRequest, response: Dashboard, summary: "Bulk move/resize widgets" }),
  pinWidget: route({ method: "POST", path: "/v1/orgs/:orgId/dashboards/pin", params: OrgP, body: PinRequest, response: PinResult, status: 201, summary: "Pin a query (e.g. a Copilot block) to a dashboard, optionally creating the dashboard" }),

  // widgets
  createWidget: route({ method: "POST", path: "/v1/orgs/:orgId/dashboards/:dashboardId/widgets", params: DashP, body: WidgetInput, response: Widget, status: 201, summary: "Add a widget" }),
  updateWidget: route({ method: "PATCH", path: "/v1/orgs/:orgId/dashboards/:dashboardId/widgets/:widgetId", params: WidgetP, body: WidgetPatch, response: Widget, summary: "Edit a widget (title, query, viz, options, layout, refresh)" }),
  deleteWidget: route({ method: "DELETE", path: "/v1/orgs/:orgId/dashboards/:dashboardId/widgets/:widgetId", params: WidgetP, response: OkR, summary: "Remove a widget" }),
  duplicateWidget: route({ method: "POST", path: "/v1/orgs/:orgId/dashboards/:dashboardId/widgets/:widgetId/duplicate", params: WidgetP, body: EmptyB, response: Widget, status: 201, summary: "Duplicate a widget (placed below the original)" }),
} as const;
